import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Progress,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {CheckIcon, CloseIcon} from "@chakra-ui/icons";
import React, {useEffect, useState} from "react";
import {FiBriefcase, FiEye, FiEyeOff, FiLock, FiMail, FiMapPin, FiPhone, FiShield, FiUser, FiUsers} from "react-icons/fi";
import {fetchSchoolListForDropdown} from "../../../common-services";

const initialUserDetails = {
    schoolId: "",
    fullName: "",
    phone: "",
    email: "",
    address: "",
    designation: "",
    description: "",
    password: "",
};

const requiredFields = ["schoolId", "fullName", "phone", "email", "address", "designation", "password"];
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddUserModal({isOpen, onClose, handleUserSubmit}) {
    const toast = useToast();

    const modalBg = useColorModeValue("white", "gray.800");
    const sectionBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
    const inputBg = useColorModeValue("white", "gray.800");
    const mutedText = useColorModeValue("gray.500", "gray.400");
    const accentBg = useColorModeValue("blue.50", "whiteAlpha.100");
    const headingColor = useColorModeValue("gray.700", "white");

    const [schoolList, setSchoolList] = useState([]);
    const [isLoadingSchools, setIsLoadingSchools] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [userDetails, setUserDetails] = useState(initialUserDetails);

    useEffect(() => {
        if (isOpen) {
            fetchSchoolsForDropdown();
        } else {
            setUserDetails(initialUserDetails);
            setErrors({});
            setTouched({});
            setShowPassword(false);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const fetchSchoolsForDropdown = async () => {
        setIsLoadingSchools(true);

        try {
            const response = await fetchSchoolListForDropdown();
            if (response.status === 200) {
                setSchoolList(response.data || []);
            }
        } catch (e) {
            console.error("Error while fetching school list:", e);
            toast({
                title: "Unable to load schools",
                description: "Please try again or refresh the page.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoadingSchools(false);
        }
    };

    const validateField = (name, value) => {
        const normalizedValue = typeof value === "string" ? value.trim() : value;

        if (requiredFields.includes(name) && !normalizedValue) {
            return "This field is required.";
        }

        if (name === "fullName" && normalizedValue && normalizedValue.length < 2) {
            return "Enter at least 2 characters.";
        }

        if (name === "phone" && normalizedValue) {
            const phoneDigits = normalizedValue.replace(/\D/g, "");
            if (phoneDigits.length < 7 || phoneDigits.length > 15) {
                return "Enter a valid phone number.";
            }
        }

        if (name === "email" && normalizedValue && !emailPattern.test(normalizedValue)) {
            return "Enter a valid email address.";
        }

        if (name === "password" && normalizedValue) {
            if (normalizedValue.length < 8) {
                return "Use at least 8 characters.";
            }
            if (!/[A-Za-z]/.test(normalizedValue) || !/\d/.test(normalizedValue)) {
                return "Use at least one letter and one number.";
            }
        }

        if (name === "description" && normalizedValue && normalizedValue.length > 500) {
            return "Keep description under 500 characters.";
        }

        return "";
    };

    const validateForm = () => {
        const nextErrors = Object.keys(userDetails).reduce((acc, key) => {
            const error = validateField(key, userDetails[key]);
            if (error) {
                acc[key] = error;
            }
            return acc;
        }, {});

        setErrors(nextErrors);
        setTouched(Object.keys(userDetails).reduce((acc, key) => ({...acc, [key]: true}), {}));
        return Object.keys(nextErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setUserDetails((prev) => ({...prev, [name]: value}));

        if (touched[name] || errors[name]) {
            setErrors((prev) => ({...prev, [name]: validateField(name, value)}));
        }
    };

    const handleBlur = (e) => {
        const {name, value} = e.target;
        setTouched((prev) => ({...prev, [name]: true}));
        setErrors((prev) => ({...prev, [name]: validateField(name, value)}));
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast({
                title: "Check required fields",
                description: "Please fix the highlighted fields before saving.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);

        try {
            await handleUserSubmit({
                ...userDetails,
                fullName: userDetails.fullName.trim(),
                phone: userDetails.phone.trim(),
                email: userDetails.email.trim(),
                address: userDetails.address.trim(),
                designation: userDetails.designation.trim(),
                description: userDetails.description.trim(),
                password: userDetails.password,
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "Error!",
                description: "Failed to save user details. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const completedRequiredFields = requiredFields.filter((field) => !validateField(field, userDetails[field])).length;
    const completionPercent = Math.round((completedRequiredFields / requiredFields.length) * 100);

    const inputProps = {
        bg: inputBg,
        borderColor,
        _hover: {borderColor: "blue.300"},
        _focus: {borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)"}
    };

    const renderField = ({name, label, placeholder, type = "text", icon, helper, rightElement}) => (
        <FormControl isRequired={requiredFields.includes(name)} isInvalid={Boolean(touched[name] && errors[name])}>
            <FormLabel fontSize="sm" fontWeight="semibold">{label}</FormLabel>
            <Flex align="center" position="relative">
                {icon && (
                    <Icon
                        as={icon}
                        color={mutedText}
                        position="absolute"
                        left="12px"
                        zIndex="1"
                    />
                )}
                <Input
                    name={name}
                    value={userDetails[name]}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    type={type}
                    pl={icon ? "38px" : undefined}
                    pr={rightElement ? "44px" : undefined}
                    {...inputProps}
                />
                {rightElement}
            </Flex>
            {helper && !errors[name] && <FormHelperText>{helper}</FormHelperText>}
            <FormErrorMessage>{errors[name]}</FormErrorMessage>
        </FormControl>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600"/>
            <ModalContent bg={modalBg} borderRadius="18px" overflow="hidden">
                <Box bg="linear-gradient(135deg, #2563EB 0%, #14B8A6 100%)" color="white">
                    <ModalHeader px={{base: 5, md: 8}} py={6}>
                        <Flex direction={{base: "column", md: "row"}} gap={4} justify="space-between">
                            <Box>
                                <HStack spacing={3} mb={2}>
                                    <Flex
                                        align="center"
                                        justify="center"
                                        w="42px"
                                        h="42px"
                                        borderRadius="12px"
                                        bg="whiteAlpha.250"
                                    >
                                        <Icon as={FiUser} boxSize={5}/>
                                    </Flex>
                                    <Badge colorScheme="teal" variant="solid" borderRadius="full" px={3}>
                                        Admin user
                                    </Badge>
                                </HStack>
                                <Text fontSize={{base: "xl", md: "2xl"}} fontWeight="bold">Add New User</Text>
                                <Text fontSize="sm" opacity={0.9} mt={1}>
                                    Assign a school, contact details, role context, and secure login access.
                                </Text>
                            </Box>
                            <Box minW={{base: "100%", md: "220px"}}>
                                <Flex justify="space-between" fontSize="sm" mb={2}>
                                    <Text>Required details</Text>
                                    <Text fontWeight="bold">{completionPercent}%</Text>
                                </Flex>
                                <Progress value={completionPercent} size="sm" borderRadius="full" bg="whiteAlpha.300" colorScheme="green"/>
                            </Box>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton color="white"/>
                </Box>

                <ModalBody px={{base: 4, md: 8}} py={6}>
                    <Stack spacing={6}>
                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                            <Flex align="center" justify="space-between" mb={5} gap={3} wrap="wrap">
                                <Box>
                                    <Text fontWeight="bold" color={headingColor}>School assignment</Text>
                                    <Text fontSize="sm" color={mutedText}>Choose which school this admin user belongs to.</Text>
                                </Box>
                                <Icon as={FiUsers} color="blue.400" boxSize={5}/>
                            </Flex>

                            <FormControl isRequired isInvalid={Boolean(touched.schoolId && errors.schoolId)}>
                                <FormLabel fontSize="sm" fontWeight="semibold">Select School</FormLabel>
                                <Select
                                    name="schoolId"
                                    placeholder={isLoadingSchools ? "Loading schools..." : "Select school"}
                                    value={userDetails.schoolId}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    isDisabled={isLoadingSchools}
                                >
                                    {schoolList.map((school, index) => (
                                        <option key={school.value || index} value={school.value}>
                                            {school.label}
                                        </option>
                                    ))}
                                </Select>
                                {!errors.schoolId && (
                                    <FormHelperText>
                                        {schoolList.length ? `${schoolList.length} school${schoolList.length === 1 ? "" : "s"} available.` : "No schools loaded yet."}
                                    </FormHelperText>
                                )}
                                <FormErrorMessage>{errors.schoolId}</FormErrorMessage>
                            </FormControl>
                        </Box>

                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                            <Flex align="center" justify="space-between" mb={5} gap={3} wrap="wrap">
                                <Box>
                                    <Text fontWeight="bold" color={headingColor}>Contact profile</Text>
                                    <Text fontSize="sm" color={mutedText}>User identity and reachable contact information.</Text>
                                </Box>
                                <Icon as={FiMail} color="blue.400" boxSize={5}/>
                            </Flex>

                            <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                                {renderField({
                                    name: "fullName",
                                    label: "Full Name",
                                    placeholder: "Enter full name",
                                    icon: FiUser,
                                })}
                                {renderField({
                                    name: "phone",
                                    label: "Phone",
                                    placeholder: "Enter phone number",
                                    type: "tel",
                                    icon: FiPhone,
                                })}
                                {renderField({
                                    name: "email",
                                    label: "Email",
                                    placeholder: "name@example.com",
                                    type: "email",
                                    icon: FiMail,
                                })}
                                {renderField({
                                    name: "designation",
                                    label: "Designation",
                                    placeholder: "e.g. Principal, IT Admin",
                                    icon: FiBriefcase,
                                })}
                                <Box gridColumn={{base: "auto", md: "1 / -1"}}>
                                    {renderField({
                                        name: "address",
                                        label: "Address",
                                        placeholder: "Enter address",
                                        icon: FiMapPin,
                                    })}
                                </Box>
                            </SimpleGrid>
                        </Box>

                        <SimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
                            <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                                <Flex align="center" justify="space-between" mb={5}>
                                    <Box>
                                        <Text fontWeight="bold" color={headingColor}>Login security</Text>
                                        <Text fontSize="sm" color={mutedText}>Create a password for first access.</Text>
                                    </Box>
                                    <Icon as={FiShield} color="blue.400" boxSize={5}/>
                                </Flex>

                                {renderField({
                                    name: "password",
                                    label: "Password",
                                    placeholder: "Create password",
                                    type: showPassword ? "text" : "password",
                                    icon: FiLock,
                                    helper: "Use 8+ characters with at least one letter and one number.",
                                    rightElement: (
                                        <IconButton
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            icon={showPassword ? <FiEyeOff/> : <FiEye/>}
                                            variant="ghost"
                                            size="sm"
                                            position="absolute"
                                            right="4px"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                        />
                                    )
                                })}
                            </Box>

                            <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                                <Flex align="center" justify="space-between" mb={5}>
                                    <Box>
                                        <Text fontWeight="bold" color={headingColor}>Admin notes</Text>
                                        <Text fontSize="sm" color={mutedText}>Optional context for internal review.</Text>
                                    </Box>
                                    <Badge bg={accentBg} color="blue.500" borderRadius="full" px={3}>
                                        Optional
                                    </Badge>
                                </Flex>

                                <FormControl isInvalid={Boolean(touched.description && errors.description)}>
                                    <FormLabel fontSize="sm" fontWeight="semibold">Description</FormLabel>
                                    <Textarea
                                        name="description"
                                        value={userDetails.description}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Add responsibilities, access notes, or onboarding context"
                                        minH="118px"
                                        resize="vertical"
                                        bg={inputBg}
                                        borderColor={borderColor}
                                    />
                                    {!errors.description && (
                                        <FormHelperText>{userDetails.description.length}/500 characters</FormHelperText>
                                    )}
                                    <FormErrorMessage>{errors.description}</FormErrorMessage>
                                </FormControl>
                            </Box>
                        </SimpleGrid>
                    </Stack>
                </ModalBody>

                <Divider/>

                <ModalFooter px={{base: 4, md: 8}} py={4}>
                    <Flex w="100%" gap={3} justify="space-between" align="center" direction={{base: "column", sm: "row"}}>
                        <HStack color={completionPercent === 100 ? "green.500" : mutedText} spacing={2}>
                            <Icon as={completionPercent === 100 ? CheckIcon : CloseIcon} boxSize={3}/>
                            <Text fontSize="sm">{completedRequiredFields}/{requiredFields.length} required fields complete</Text>
                        </HStack>

                        <HStack spacing={3} w={{base: "100%", sm: "auto"}}>
                            <Button variant="ghost" onClick={onClose} w={{base: "50%", sm: "auto"}} isDisabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleSubmit}
                                w={{base: "50%", sm: "auto"}}
                                isLoading={isSubmitting}
                                isDisabled={isLoadingSchools}
                            >
                                Save User
                            </Button>
                        </HStack>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AddUserModal;
